import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Layout } from "../components/tag/Layout";
import { api } from "../lib/api";

export default function AuditConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const productParam = params.get("product") || "";
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [product, setProduct] = useState(productParam);

  useEffect(() => {
    if (!sessionId) {
      setStatus("paid");
      return;
    }
    let attempts = 0;
    const endpoint = productParam && productParam !== "audit"
      ? `/checkout/status/${sessionId}`
      : `/audit/status/${sessionId}`;
    const poll = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (data.email) setEmail(data.email);
        if (data.product) setProduct(data.product);
        if (data.payment_status === "paid") {
          setStatus("paid");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 8) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
  }, [sessionId, productParam]);

  const isBlueprint = product === "blueprint";
  const intakePath = isBlueprint ? "/blueprint/intake" : "/audit/intake";
  const productLabel = isBlueprint ? "Authority Blueprint" : "Authority Audit";

  return (
    <Layout title={`${productLabel} Confirmed | The Authority Gap`} path="/audit/confirmed">
      <section style={{ background: "#090909", padding: "80px 32px", textAlign: "center" }}>
        <div className="tag-card" data-testid="audit-confirmed-card" style={{ maxWidth: 520, margin: "0 auto", padding: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
            <CheckCircle2 size={48} color="#FF6575" />
          </div>
          {status === "paid" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>You&apos;re confirmed.</h2>
              <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
                {isBlueprint
                  ? "Complete the short intake form below and we'll generate your Blueprint Summary in minutes. Your full Authority Blueprint follows within 3-5 business days."
                  : "Complete the short intake form below and we'll generate your branded Authority Audit in minutes."}
              </p>
              <Link
                to={email ? `${intakePath}?email=${encodeURIComponent(email)}` : intakePath}
                data-testid="audit-confirmed-intake-link"
                className="btn-primary"
                style={{ display: "inline-block", marginBottom: 12 }}
              >
                Complete the intake form
              </Link>
              <div>
                <Link to="/" data-testid="audit-confirmed-home" style={{ color: "#777", fontSize: 12 }}>
                  ← Return to The Authority Gap
                </Link>
              </div>
            </>
          )}
          {status === "checking" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Confirming payment…</h2>
              <p style={{ color: "#bbb" }}>Hold tight for a moment.</p>
            </>
          )}
          {(status === "timeout" || status === "error") && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Still verifying…</h2>
              <p style={{ color: "#bbb", fontSize: 13 }}>Your payment may still be processing. If you don&apos;t get a confirmation email within a few minutes, contact support@theauthoritygap.co.</p>
            </>
          )}
          {status === "expired" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Payment session expired</h2>
              <Link to="/audit" className="btn-primary">Try again</Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
